import posthog from 'posthog-js';
import axios from 'axios';

type SharedPosthogProps = {
    api_key?: string;
    api_host?: string;
    ui_host?: string;
    distinct_id?: string | null;
    person_properties?: Record<string, unknown> | null;
    team_group_key?: string | null;
    team_properties?: Record<string, unknown> | null;
};

type SharedProps = {
    user?: { id: number | string; email?: string; name?: string } | null;
    team?: { id: number | string; name?: string } | null;
    posthog?: SharedPosthogProps;
};

let initialized = false;
let lastIdentifiedId: string | null = null;

function getShared(): SharedProps {
    const el = document.getElementById('app');
    const raw = el?.dataset.page;
    if (!raw) {
        return {};
    }
    try {
        const parsed = JSON.parse(raw) as { props?: SharedProps };
        return parsed.props ?? {};
    } catch {
        return {};
    }
}

export function initPostHog(): typeof posthog | null {
    if (initialized) {
        return posthog;
    }

    const shared = getShared();
    const apiKey = shared.posthog?.api_key;
    const apiHost = shared.posthog?.api_host || 'https://i.topol.io';
    const uiHost = shared.posthog?.ui_host || 'https://eu.posthog.com';

    if (!apiKey) {
        return null;
    }

    posthog.init(apiKey, {
        api_host: apiHost,
        ui_host: uiHost,
        defaults: '2026-01-30',
        person_profiles: 'always',
        capture_pageview: true,
        capture_pageleave: true,
        cross_subdomain_cookie: true,
        autocapture: true,
        capture_exceptions: false,
        loaded: (ph) => {
            axios.interceptors.request.use((config) => {
                const distinctId = ph.get_distinct_id();
                const sessionId = ph.get_session_id?.();
                if (distinctId) {
                    config.headers = config.headers ?? {};
                    (config.headers as Record<string, string>)['X-POSTHOG-DISTINCT-ID'] = distinctId;
                    if (sessionId) {
                        (config.headers as Record<string, string>)['X-POSTHOG-SESSION-ID'] =
                            sessionId;
                    }
                }
                return config;
            });
        },
    });

    initialized = true;
    identifyFromSharedProps(shared);

    return posthog;
}

export function identifyFromSharedProps(shared: SharedProps = getShared()): void {
    if (!initialized) {
        return;
    }

    const distinctId = shared.posthog?.distinct_id ?? null;
    const personProperties = shared.posthog?.person_properties ?? null;
    const teamGroupKey = shared.posthog?.team_group_key ?? null;
    const teamProperties = shared.posthog?.team_properties ?? null;

    if (!distinctId) {
        if (lastIdentifiedId !== null) {
            resetPostHog();
        }
        return;
    }

    if (distinctId !== lastIdentifiedId) {
        posthog.identify(
            distinctId,
            personProperties ?? {
                email: shared.user?.email,
                name: shared.user?.name,
            },
        );
        lastIdentifiedId = distinctId;
    }

    registerEventIdentity(shared);

    if (teamGroupKey) {
        posthog.group('team', teamGroupKey, teamProperties ?? { name: shared.team?.name });
    }
}

/**
 * Person properties live on the PostHog person profile and are not joined onto
 * event rows by the BigQuery export, so the identity has to ride along on every
 * event. Registering them as super properties covers custom events, autocapture
 * and pageviews alike; `posthog.reset()` clears them on logout.
 */
function registerEventIdentity(shared: SharedProps): void {
    const identity: Record<string, unknown> = {};

    const userId = shared.user?.id;
    if (userId !== undefined && userId !== null) {
        identity.user_id = userId;
    }

    const personEmail = shared.posthog?.person_properties?.email;
    const email = shared.user?.email ?? (typeof personEmail === 'string' ? personEmail : undefined);
    if (email) {
        identity.email = email;
    }

    if (Object.keys(identity).length > 0) {
        posthog.register(identity);
    }
}

export function resetPostHog(): void {
    if (!initialized) {
        return;
    }
    posthog.reset();
    lastIdentifiedId = null;
}

export function capturePostHogEvent(
    eventName: string,
    properties: Record<string, unknown> = {},
): void {
    if (!posthog.__loaded) {
        return;
    }
    posthog.capture(eventName, properties);
}

export { posthog };
